import { lazy, Suspense, useLayoutEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

const Home          = lazy(() => import('./pages/Home'));
const ProA          = lazy(() => import('./pages/ProA'));
const ProB          = lazy(() => import('./pages/ProB'));
const ProC          = lazy(() => import('./pages/ProC'));
const ProD          = lazy(() => import('./pages/ProD'));
const Basic         = lazy(() => import('./pages/Basic'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

/* On every route change, jump instantly to the top of the new page.
   Runs in a layout effect (before paint) and temporarily disables the global
   `scroll-behavior: smooth`, so the destination page always opens at its start
   instead of inheriting the previous page's scroll position (or getting stuck
   mid-page by an interrupted smooth-scroll animation).

   Because pages are lazy-loaded, the destination content often mounts a frame
   or two AFTER this effect runs (while Suspense shows its fallback). A single
   sync reset can therefore land on an empty document and be "lost" once the
   tall page paints. To guarantee top:0 on every navigation we reset now and
   again across the next few animation frames, until the new page has painted.
   Hash links (same-page section scroll) are left untouched. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if (hash) return undefined;

    const html = document.documentElement;
    const jump = () => {
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      html.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      html.style.scrollBehavior = prevBehavior;
    };

    jump();
    let frame;
    let count = 0;
    const settle = () => {
      jump();
      if (++count < 5) frame = requestAnimationFrame(settle);
    };
    frame = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <Suspense fallback={null}>
      <ScrollToTop />
      <Routes>
        <Route path="/"              element={<Home />} />
        <Route path="/pro-a"         element={<ProA />} />
        <Route path="/pro-b"         element={<ProB />} />
        <Route path="/pro-c"         element={<ProC />} />
        <Route path="/pro-d"         element={<ProD />} />
        <Route path="/levelup"       element={<ProD />} />
        <Route path="/basic"         element={<Basic />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </Suspense>
  );
}

export default App;
